/**
 * HouseLinkModal — HOUSE-MANUAL-01. The address key misses sometimes (a typo'd
 * street, a renamed road, "Hwy 7" vs "Highway 7"), and then no question ever
 * appears. From the 360's ··· menu the office links the house by hand: search
 * the other account, pick which of its locations is this house, read the one
 * sentence that says what will happen, confirm. Same RPC as the yellow strip
 * (house_link_answer · same_house), so the result is identical — and undoable.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Home, Loader2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { CustomerPicker, type PickedCustomer } from "@/components/CustomerPicker";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { useHouseAnswer, COMPANY_RE } from "./houseHistory";

interface Loc { locno: string; add1: string | null; city: string | null; locname: string | null }

function useLocationsOf(custno: string | null) {
  return useQuery({
    queryKey: ["house_link_locs", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Loc[]> => {
      const { data, error } = await supabase.from("mirror_location").select("locno, add1, city, locname").eq("custno", custno!).order("locno");
      if (error) throw error;
      return (data ?? []) as Loc[];
    },
  });
}

const norm = (s: string | null | undefined) => (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

export function HouseLinkModal({ custno, locno, address, customerName, onClose }: {
  custno: string;
  locno: string | null;
  address: string | null;
  customerName: string;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<PickedCustomer | null>(null);
  const [pickLoc, setPickLoc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const { data: locs, isLoading } = useLocationsOf(picked?.custno ?? null);
  const answer = useHouseAnswer();
  const isCompany = !!picked && COMPANY_RE.test(picked.name);

  // Preselect the location that looks like this house (same house number), else the only one.
  useEffect(() => {
    if (!locs) return;
    if (locs.length === 1) { setPickLoc(locs[0].locno); return; }
    const num = (address ?? "").trim().split(/\s+/)[0];
    const hit = num ? locs.find((l) => (l.add1 ?? "").trim().startsWith(num)) : null;
    setPickLoc(hit?.locno ?? null);
  }, [locs, address]);

  const chosen = useMemo(() => (locs ?? []).find((l) => l.locno === pickLoc) ?? null, [locs, pickLoc]);
  const sameAddress = chosen && norm(chosen.add1) === norm(address);

  const link = () => {
    if (!picked || !chosen) return;
    setErr(null);
    answer.mutate(
      { custno, locno, otherCustno: picked.custno, otherLocno: chosen.locno, answer: "same_house", note: "linked by hand" },
      { onSuccess: () => onClose(), onError: (e) => setErr(e instanceof Error ? e.message : String(e)) },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Link another account to this house"
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!picked || !chosen || answer.isPending} onClick={link}>
            {answer.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Home className="h-3.5 w-3.5" />} Link as the same house
          </Button>
        </div>
      }
    >
      {/* room for the picker's dropdown — the modal body scrolls, and an absolute list below the box would be clipped */}
      <div className={cn("space-y-3", !picked && "min-h-[24rem]")}>
        <p className="text-sm text-slate-600">
          <b className="text-slate-800">{address ?? "This address"}</b> — {customerName}&apos;s. Pick the other account that has served this same house (the builder, the previous owner, a landlord). Their visits and units will read here as the house&apos;s history; nothing moves and ESC is untouched.
        </p>
        <CustomerPicker onSelect={(c) => { setPicked(c); setPickLoc(null); }} selected={picked} onClear={() => { setPicked(null); setPickLoc(null); }} excludeCustno={custno} autoFocus />
        {picked && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Which of their locations is this house?</div>
            {isLoading ? (
              <div className="mt-1.5 h-9 animate-pulse rounded-lg bg-slate-100" />
            ) : (locs ?? []).length === 0 ? (
              <p className="mt-1.5 text-xs text-slate-500">No locations on file for that account.</p>
            ) : (
              <div className="mt-1.5 max-h-56 space-y-1 overflow-y-auto">
                {(locs ?? []).map((l) => {
                  const on = l.locno === pickLoc;
                  return (
                    <button key={l.locno} type="button" onClick={() => setPickLoc(l.locno)}
                      className={cn("flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm", on ? "border-slate-900 bg-slate-50" : "border-hairline bg-white hover:border-slate-300")}>
                      <span className={cn("h-3.5 w-3.5 shrink-0 rounded-full border", on ? "border-slate-900 bg-slate-900" : "border-slate-300")} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-slate-800">{l.add1 ?? l.locname ?? `Location ${l.locno}`}</span>
                        <span className="block truncate text-[11px] text-slate-400">{[l.city, `loc ${l.locno}`, l.locname && l.locname !== l.add1 ? l.locname : null].filter(Boolean).join(" · ")}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {picked && chosen && (
          <div className={cn("rounded-lg px-3 py-2 text-xs leading-relaxed", sameAddress ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900")}>
            {sameAddress ? "Same address on both accounts." : <>Their address reads <b>{chosen.add1 ?? "—"}</b>; this one reads <b>{address ?? "—"}</b>. Link only if you know it is the same house.</>}
            {" "}<b>{picked.name}</b> will read as the {isCompany ? "builder" : "previous owner"} here; {customerName} as the owner since this account appeared.
          </div>
        )}
        {err && <p className="text-xs font-medium text-rose-600">{err}</p>}
      </div>
    </Modal>
  );
}
